import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import type { ErrorLike } from "@apollo/client";
import type { Color } from "@salestrak/ui";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { WidgetIssueStageProgressionDocument, type StageType } from "#app/lib/data";
import {
  useWorkspaceData,
  type WorkspaceStage,
  type WorkspaceWorkflow,
} from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";
import { useTeamScope } from "../shared/use-team-scope";

/** One step of a pipeline, as handed to {@link buildStageConversion}. */
export type StageConversionInput = {
  label: string;
  /** The projects sitting in this step now — the figure the band prints. */
  count: number;
  /** The projects that ever entered this step — the base the rate divides. */
  reached: number;
  /** Of `reached`, the projects that went on to a later step of the pipeline
   *  (the next one or any after it). */
  advanced: number;
  /** Average time from entering this step to entering the next step the
   *  project reached, in seconds. */
  durationToNext?: number | undefined;
};

/** A step with its yield read: the share of the projects that reached this
 *  step which went on past it, as a percentage rounded to one decimal.
 *  `null` on the last step (nothing follows it) and on a step nothing has
 *  reached (no base). */
export type StageConversionStep<T extends StageConversionInput = StageConversionInput> = T & {
  conversionToNext: number | null;
};

/**
 * Read a pipeline's conversion, step to step. Pure: the order of `steps` IS
 * the pipeline, so the output keeps it and every other field the caller put
 * on a step rides through untouched (`durationToNext` included, only where
 * it was given). The rate is `step.advanced / step.reached * 100`: of the
 * projects that ever entered a step, the share that later entered a step
 * after it — the next one, or any later one when they skipped ahead. A
 * project that never visited a step is not in its base, so the rate never
 * exceeds 100.
 */
export function buildStageConversion<T extends StageConversionInput>(
  steps: readonly T[],
): StageConversionStep<T>[] {
  return steps.map((step, index) => {
    const next = steps[index + 1];
    const conversionToNext =
      next === undefined || step.reached <= 0
        ? null
        : Math.round((step.advanced / step.reached) * 1000) / 10;
    return { ...step, conversionToNext };
  });
}

/** A workflow's step, with the identity the band needs to color and drill it. */
export type WorkflowConversionStep = StageConversionStep<{
  id: string;
  label: string;
  color: Color | null;
  count: number;
  reached: number;
  advanced: number;
  /** The projects that entered the pipeline's first step and later this one. */
  fromFirst: number;
  durationToNext?: number | undefined;
}>;

export type WorkflowConversion = {
  workflow: WorkspaceWorkflow;
  /** The steps in pipeline order: the workflow's chosen steps, else every
   *  stage of the workflow but its canceled ones. */
  steps: WorkflowConversionStep[];
  /** Every project in scope in the workflow, canceled included. */
  total: number;
  /** Average time from entering the first step to entering the last, over
   *  the projects that did both, in seconds. `null` while no project has. */
  totalDurationSeconds: number | null;
};

/**
 * The whole pipeline's yield as one figure: of the projects that entered the
 * first step, the share that later reached the last, as a percentage rounded
 * to one decimal. `null` when the pipeline has fewer than two steps (no
 * distance to convert across) or nothing has entered the first step (no
 * base). Pure, over the same flow the step rates divide.
 */
export function pipelineConversion(
  steps: readonly { reached: number; fromFirst: number }[],
): number | null {
  const first = steps[0];
  const last = steps[steps.length - 1];
  if (first === undefined || last === undefined || steps.length < 2) return null;
  if (first.reached <= 0) return null;
  return Math.round((last.fromFirst / first.reached) * 1000) / 10;
}

/** The one stage category that is not a step: a canceled project left the
 *  pipeline, it did not reach a stage of it. */
const CANCELED: StageType = "canceled";

/** The fewest steps a pipeline can convert across. */
export const MIN_CONVERSION_STEPS = 2;

/**
 * The steps a workflow's strip prints, in the workflow's stage order. With a
 * chosen list, the workflow's non-canceled stages that are on it; a stage
 * the list names that the workflow no longer has is dropped silently. Fewer
 * than {@link MIN_CONVERSION_STEPS} left (or no list) falls back to every
 * non-canceled stage, so the strip always has a pipeline to read.
 */
export function resolveConversionSteps(
  stages: readonly WorkspaceStage[],
  chosen: readonly string[] | null | undefined,
): WorkspaceStage[] {
  const open = stages.filter((s) => s.type !== CANCELED);
  if (!chosen) return open;
  const wanted = new Set(chosen);
  const picked = open.filter((s) => wanted.has(s.stageId));
  return picked.length >= MIN_CONVERSION_STEPS ? picked : open;
}

type ProgressionStat = {
  stageId: string;
  reached: number;
  advanced: number;
  avgSecondsToNext: number;
  fromFirst: number;
  avgSecondsFromFirst: number;
};

/**
 * Fold the two per-stage reads into one conversion strip per workflow. Pure,
 * so the widget's shape decisions are testable without a provider:
 *
 * - `workflows` are printed in the order given; a workflow's steps are
 *   {@link resolveConversionSteps} over its stages (the order
 *   `stagesByWorkflow` hands over) and its entry in `stepsByWorkflow`.
 * - Canceled stages are never steps and are kept in `total`.
 * - A workflow with nothing in scope drops unless `keepEmpty` (one workflow was
 *   chosen, so "nothing in it" is the answer).
 * - `reached`/`advanced`/`fromFirst` come off the progression read; a step it
 *   has no row for reads 0, so its rate is `null` rather than a guess off the
 *   live count.
 * - `durationToNext` is set on every step but the last, and only where some
 *   project advanced (an average over nobody is not a time).
 */
export function buildWorkflowConversions(input: {
  workflows: readonly WorkspaceWorkflow[];
  stagesByWorkflow: ReadonlyMap<string, readonly WorkspaceStage[]>;
  stepsByWorkflow: ReadonlyMap<string, readonly string[] | null>;
  keepEmpty: boolean;
  totals: Record<string, number>;
  progression: readonly ProgressionStat[];
}): WorkflowConversion[] {
  const byStage = new Map(input.progression.map((p) => [p.stageId, p]));
  const out: WorkflowConversion[] = [];
  for (const workflow of input.workflows) {
    const stages = input.stagesByWorkflow.get(workflow.workflowId) ?? [];
    const total = stages.reduce((sum, s) => sum + (input.totals[s.stageId] ?? 0), 0);
    if (total === 0 && !input.keepEmpty) continue;
    const open = resolveConversionSteps(stages, input.stepsByWorkflow.get(workflow.workflowId));
    const steps = buildStageConversion(
      open.map((s, index) => {
        const p = byStage.get(s.stageId);
        const measured = p !== undefined && p.advanced > 0 && index < open.length - 1;
        return {
          id: s.stageId,
          label: s.name,
          color: (s.icon?.color as Color | null | undefined) ?? null,
          count: input.totals[s.stageId] ?? 0,
          reached: p?.reached ?? 0,
          advanced: p?.advanced ?? 0,
          fromFirst: p?.fromFirst ?? 0,
          ...(measured ? { durationToNext: p.avgSecondsToNext } : {}),
        };
      }),
    );
    const last = open[open.length - 1];
    const lastStat = last ? byStage.get(last.stageId) : undefined;
    const totalDurationSeconds =
      open.length >= MIN_CONVERSION_STEPS && lastStat !== undefined && lastStat.fromFirst > 0
        ? lastStat.avgSecondsFromFirst
        : null;
    out.push({ workflow, steps, total, totalDurationSeconds });
  }
  return out;
}

/**
 * The strip's data: two exact reads over the scoped predicate, folded per
 * workflow. Per-stage counts come from `issueGroupCounts`; the flow the rates
 * divide and the time between steps from `issueStageProgression` over the
 * steps of every workflow in reach, in ONE query (a hop only lands on a later
 * step of the same workflow, so the lists can share it). The workflows are
 * the board's reach — the chosen workflow, else a workflow board's own
 * workflow (every workflow has its own conversion; another workflow's strip
 * on its board is noise), else a team board's granted workflows, else every
 * live workflow — in the workspace's order. `steps` names the chosen
 * workflow's steps and applies to it alone; the other workflows print every
 * stage. The widget's Filters and its workflow choice AND into the
 * dashboard's own scope.
 */
export function useStageConversion(
  workflowId: string | null | undefined,
  steps: readonly string[] | null | undefined,
  filters: unknown,
): { data: WorkflowConversion[]; loading: boolean; error?: ErrorLike | undefined } {
  const { base: filterBase, override } = useWidgetFilters(filters);
  const base = useMemo(
    () =>
      combineWhereConditions([
        filterBase,
        ...(workflowId ? [{ workflowId: { eq: workflowId } }] : []),
      ]) ?? {},
    [filterBase, workflowId],
  );
  const { where } = useScopedWhere(base, { override });
  const { workflows, workflowMap, stagesByWorkflow } = useWorkspaceData();
  const scope = useDashboardScope();
  const scopeWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : null;
  const chosenWorkflowId = workflowId ?? scopeWorkflowId;
  const { teamId, team, loading: teamPending, error: teamError } = useTeamScope();

  const reach = useMemo<WorkspaceWorkflow[]>(() => {
    if (chosenWorkflowId) {
      const one = workflowMap.get(chosenWorkflowId);
      return one ? [one] : [];
    }
    const live = workflows.filter((w) => !w.archived);
    if (!teamId) return live;
    const granted = new Set(team?.workflowIds ?? []);
    return live.filter((w) => granted.has(w.workflowId));
  }, [chosenWorkflowId, workflowMap, workflows, teamId, team]);

  const stepsByWorkflow = useMemo(
    () =>
      new Map<string, readonly string[] | null>(
        chosenWorkflowId ? [[chosenWorkflowId, steps ?? null]] : [],
      ),
    [chosenWorkflowId, steps],
  );

  // The one list the progression read walks: every workflow's steps, each
  // workflow's in its stage order.
  const stageIds = useMemo(
    () =>
      reach.flatMap((w) =>
        resolveConversionSteps(
          stagesByWorkflow.get(w.workflowId) ?? [],
          stepsByWorkflow.get(w.workflowId),
        ).map((s) => s.stageId),
      ),
    [reach, stagesByWorkflow, stepsByWorkflow],
  );

  const totals = useIssueGroupCounts({ where, groupBy: "stageId" });
  const progressionVariables = useMemo(() => ({ where, stageIds }), [where, stageIds]);
  const progression = useQuery(WidgetIssueStageProgressionDocument, {
    variables: progressionVariables,
    fetchPolicy: "cache-and-network",
    skip: stageIds.length === 0,
  });

  const data = useMemo(
    () =>
      buildWorkflowConversions({
        workflows: reach,
        stagesByWorkflow,
        stepsByWorkflow,
        keepEmpty: Boolean(chosenWorkflowId),
        totals: totals.counts,
        progression: progression.data?.issueStageProgression ?? [],
      }),
    [reach, stagesByWorkflow, stepsByWorkflow, chosenWorkflowId, totals.counts, progression.data],
  );

  return {
    data,
    loading:
      teamPending || !totals.ready || (stageIds.length > 0 && progression.data === undefined),
    error: teamError ?? totals.error ?? progression.error,
  };
}
